import type { Id } from "@repo/convex/dataModel";
import { resolveEntityAuthoringTabDescriptors } from "@repo/shared";
import { useMemo } from "react";
import { useProjectFrameworkStructure } from "../../hooks";

export type SectionLabels = { tabLabel?: string; fieldLabel?: string };

/**
 * The tab and section labels of an element type, from the compiled framework structure the
 * entity page uses, and each section's position in the template so lists can follow it. Before
 * the structure loads, or for an unknown type, every lookup answers nothing and callers fall
 * back to humanized keys.
 */
export function useSectionLabels(projectId: Id<"projects">, entityTypeKey: string) {
  const frameworkStructure = useProjectFrameworkStructure(projectId);
  return useMemo(() => {
    let tabs: ReturnType<typeof resolveEntityAuthoringTabDescriptors> | null = null;
    try {
      tabs = resolveEntityAuthoringTabDescriptors({
        entityTypeKey,
        compiledStructure: frameworkStructure ?? null,
        compiledTemplateJson: frameworkStructure?.compiledTemplateJson ?? null,
      });
    } catch {
      tabs = null;
    }
    const labels = (
      tabKey: string | null | undefined,
      fieldId: string | null | undefined
    ): SectionLabels => {
      if (!tabs || !tabKey) {
        return {};
      }
      const tab = tabs.find((candidate) => candidate.tabKey === tabKey);
      return {
        tabLabel: tab?.label,
        fieldLabel: fieldId
          ? tab?.sections.find((section) => section.fieldId === fieldId)?.label
          : undefined,
      };
    };
    /** Template order: tabs first, then sections within a tab; unknown places sort last. */
    const position = (tabKey: string | null | undefined, fieldId: string | null | undefined) => {
      const tabIndex = tabs?.findIndex((candidate) => candidate.tabKey === tabKey) ?? -1;
      if (tabIndex < 0) return Number.MAX_SAFE_INTEGER;
      const sectionIndex = fieldId
        ? (tabs?.[tabIndex]?.sections.findIndex((section) => section.fieldId === fieldId) ?? -1)
        : -1;
      return tabIndex * 10_000 + (sectionIndex < 0 ? 0 : sectionIndex + 1);
    };
    return { labels, position };
  }, [entityTypeKey, frameworkStructure]);
}

/**
 * The same labels for every element type of the project, for a view that lists sections of
 * many types: `labels(entityTypeKey, tabKey, fieldId)`.
 */
export function useProjectSectionLabels(projectId: Id<"projects">) {
  const frameworkStructure = useProjectFrameworkStructure(projectId);
  return useMemo(() => {
    const byType = new Map<
      string,
      ReturnType<typeof resolveEntityAuthoringTabDescriptors> | null
    >();
    const tabsOf = (entityTypeKey: string) => {
      if (!byType.has(entityTypeKey)) {
        let tabs: ReturnType<typeof resolveEntityAuthoringTabDescriptors> | null = null;
        try {
          tabs = resolveEntityAuthoringTabDescriptors({
            entityTypeKey,
            compiledStructure: frameworkStructure ?? null,
            compiledTemplateJson: frameworkStructure?.compiledTemplateJson ?? null,
          });
        } catch {
          tabs = null;
        }
        byType.set(entityTypeKey, tabs);
      }
      return byType.get(entityTypeKey) ?? null;
    };
    return (
      entityTypeKey: string,
      tabKey: string | null | undefined,
      fieldId: string | null | undefined
    ): SectionLabels => {
      const tabs = tabsOf(entityTypeKey);
      if (!tabs || !tabKey) return {};
      const tab = tabs.find((candidate) => candidate.tabKey === tabKey);
      return {
        tabLabel: tab?.label,
        fieldLabel: fieldId
          ? tab?.sections.find((section) => section.fieldId === fieldId)?.label
          : undefined,
      };
    };
  }, [frameworkStructure]);
}
